import { useMap } from "@react-hookz/web";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { page as PlusSearchPage } from "scmp-app/pages/plus/search";
import {
  page as SearchPage,
  type RouteFunction as SearchRouteFunction,
} from "scmp-app/pages/search";
import type {
  ArticlesSearchOrderByInput,
  ContentTypeEnum,
} from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

import { DefaultContentTypes } from "./consts";

export type ArticleResultIdMapEntry = {
  page: number;
  position: number;
  resultId?: null | string;
};

export type ArticlesSearchFilterByDateRange = {
  endDate?: number;
  startDate?: number;
};

type ContextValue = {
  articleResultIdMap?: Map<string, ArticleResultIdMapEntry>;
  articleTypeIds?: string[];
  articleTypeText?: string;
  contentTypes?: ContentTypeEnum[];
  currentValue: string;
  dateRange?: ArticlesSearchFilterByDateRange;
  dateRangeText?: string;
  initialSearchId?: string;
  orderBy?: ArticlesSearchOrderByInput;
  paywallTypeIds?: string[];
  searchPageType: SearchPageType;
  searchRouteFunction?: SearchRouteFunction;
  sectionIds?: string[];
  setArticleTypeIds?: Dispatch<SetStateAction<string[]>>;
  setArticleTypeText?: Dispatch<SetStateAction<string>>;
  setContentTypes?: Dispatch<SetStateAction<ContentTypeEnum[]>>;
  setDateRange?: Dispatch<SetStateAction<ArticlesSearchFilterByDateRange | undefined>>;
  setDateRangeText?: Dispatch<SetStateAction<string>>;
  setInitialSearchId?: Dispatch<SetStateAction<string | undefined>>;
  setOrderBy?: Dispatch<SetStateAction<ArticlesSearchOrderByInput | undefined>>;
  setPaywallTypeIds?: Dispatch<SetStateAction<string[]>>;
  setSectionIds?: Dispatch<SetStateAction<string[]>>;
};

type SearchPageType = "default" | "plus";

export const SearchContext = createContext<ContextValue>({
  currentValue: "",
  searchPageType: "default",
});

type Props = {
  children: ReactNode;
  currentValue: string;
  searchPageType: SearchPageType;
};

export const SearchContextProvider: React.FunctionComponent<Props> = ({
  children,
  currentValue,
  searchPageType,
}) => {
  const [articleTypeIds, setArticleTypeIds] = useState<string[]>([]);
  const [articleTypeText, setArticleTypeText] = useState<string>("All");
  const [contentTypes, setContentTypes] = useState<ContentTypeEnum[]>(DefaultContentTypes);
  const [dateRange, setDateRange] = useState<ArticlesSearchFilterByDateRange | undefined>();
  const [dateRangeText, setDateRangeText] = useState<string>("All");
  const [orderBy, setOrderBy] = useState<ArticlesSearchOrderByInput>();
  const [paywallTypeIds, setPaywallTypeIds] = useState<string[]>([]);
  const [sectionIds, setSectionIds] = useState<string[]>([]);

  const [initialSearchId, setInitialSearchId] = useState<string>();
  const articleResultIdMap = useMap<string, ArticleResultIdMapEntry>([]);

  const searchRouteFunction = useMemo(
    () => (searchPageType === "default" ? SearchPage.route : PlusSearchPage.route),
    [searchPageType],
  );

  useEffect(() => {
    if (currentValue) return;
    // Clear effect when the search input has been cleared
    setArticleTypeIds([]);
    setContentTypes(DefaultContentTypes);
    setDateRange(undefined);
    setOrderBy(undefined);
    setPaywallTypeIds([]);
    setSectionIds([]);

    setInitialSearchId(undefined);
    articleResultIdMap.clear();
  }, [articleResultIdMap, currentValue, searchPageType]);

  return (
    <SearchContext.Provider
      value={{
        articleResultIdMap,
        articleTypeIds,
        articleTypeText,
        contentTypes,
        currentValue,
        dateRange,
        dateRangeText,
        initialSearchId,
        orderBy,
        paywallTypeIds,
        searchPageType,
        searchRouteFunction,
        sectionIds,
        setArticleTypeIds,
        setArticleTypeText,
        setContentTypes,
        setDateRange,
        setDateRangeText,
        setInitialSearchId,
        setOrderBy,
        setPaywallTypeIds,
        setSectionIds,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);
